import { defineStore } from 'pinia';
import type {
  ConsoleEntry,
  NetworkEntry,
  ErrorEntry,
  BrowserInfo,
  LogData,
  ConsoleFilter,
  NetworkFilter,
  LogSubTab,
  LogsLoadStatus,
} from '@/modules/viewer/types/logs';
import { downloadZip, extract, InsightsNetworkError, InsightsCorruptZipError, InsightsMissingFileError } from '@/services/insightsZipExtractor';
import { normalize } from '@/services/insightsTransformer';
import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useDebug } from '@/composables/useDebug';

const { debug } = useDebug('viewer:logs');

interface LogsState {
  // State machine
  status: LogsLoadStatus;
  errorMessage: string;
  errorRetryable: boolean;
  /** URL the current data was loaded from — cache key + race guard. */
  loadedFromUrl: string;
  /** AbortController for the in-flight fetch (non-reactive — hand-managed). */
  abortController: AbortController | null;

  // Parsed data
  consoleEntries: ConsoleEntry[];
  networkEntries: NetworkEntry[];
  errorEntries: ErrorEntry[];
  browserInfo: BrowserInfo;

  // Network detail panel — null = list view, set = detail view
  selectedNetworkEntryId: string | null;

  // Filters
  consoleFilter: ConsoleFilter;
  networkFilter: NetworkFilter;
  // Search state (per sub-tab)
  consoleSearch: string;
  networkSearch: string;
  errorsSearch: string;
}

const emptyBrowserInfo = (): BrowserInfo => ({
  userAgent: '',
  platform: '',
  language: '',
  screenResolution: '',
  viewportSize: '',
  timezone: '',
});

/**
 * Map a thrown error to a user-facing message + retryability flag.
 * Network failures get a Retry button; corrupt-data errors don't (retrying
 * won't help). Single function, no class hierarchy.
 */
const describeError = (e: unknown): { message: string; retryable: boolean; kind: string } => {
  if (e instanceof InsightsNetworkError) {
    return { kind: 'network', message: "Couldn't download logs. Check your connection and retry.", retryable: true };
  }
  if (e instanceof InsightsMissingFileError) {
    return { kind: 'missing_files', message: 'Logs archive is missing expected data.', retryable: false };
  }
  if (e instanceof InsightsCorruptZipError) {
    return { kind: 'corrupt_zip', message: 'Logs archive is corrupt or unreadable.', retryable: false };
  }
  if (e instanceof SyntaxError) {
    return { kind: 'parse_error', message: 'Logs archive contains invalid data.', retryable: false };
  }
  return { kind: 'unknown', message: 'Failed to load logs.', retryable: true };
};

/**
 * Viewer logs store. Loads `insights.zip` lazily from the artifact CDN URL,
 * unzips it client-side via JSZip, normalizes the contents into the frontend
 * `LogData` shape, and exposes filter/search/detail state for the UI.
 *
 * Cached on first successful load — switching modes (Video → Logs → Video)
 * does not re-parse. Cache invalidates when the loaded URL changes (new
 * artifact) or the consumer calls `reset()` (typically on item navigation
 * from `ViewerModule.vue`'s item-change watcher).
 */
export const useLogsStore = defineStore('viewerLogs', {
  state: (): LogsState => ({
    status: 'idle',
    errorMessage: '',
    errorRetryable: true,
    loadedFromUrl: '',
    abortController: null,

    consoleEntries: [],
    networkEntries: [],
    errorEntries: [],
    browserInfo: emptyBrowserInfo(),

    selectedNetworkEntryId: null,

    consoleFilter: 'all',
    networkFilter: 'all',
    consoleSearch: '',
    networkSearch: '',
    errorsSearch: '',
  }),

  getters: {
    consoleCounts: (state): Record<ConsoleFilter, number> => {
      const counts: Record<ConsoleFilter, number> = {
        all: state.consoleEntries.length,
        error: 0,
        warn: 0,
        info: 0,
        debug: 0,
        log: 0,
      };
      for (const entry of state.consoleEntries) {
        counts[entry.level]++;
      }
      return counts;
    },

    filteredConsoleEntries: (state): ConsoleEntry[] => {
      const query = state.consoleSearch.trim().toLowerCase();
      return state.consoleEntries.filter(entry => {
        if (state.consoleFilter !== 'all' && entry.level !== state.consoleFilter) {
          return false;
        }
        if (query && !entry.message.toLowerCase().includes(query)) {
          return false;
        }
        return true;
      });
    },

    networkCounts: (state): Record<NetworkFilter, number> => {
      const counts: Record<NetworkFilter, number> = {
        all: state.networkEntries.length,
        xhr: 0,
        asset: 0,
      };
      for (const entry of state.networkEntries) {
        counts[entry.type]++;
      }
      return counts;
    },

    filteredNetworkEntries: (state): NetworkEntry[] => {
      const query = state.networkSearch.trim().toLowerCase();
      return state.networkEntries.filter(entry => {
        if (state.networkFilter !== 'all' && entry.type !== state.networkFilter) {
          return false;
        }
        if (query && !entry.url.toLowerCase().includes(query)) {
          return false;
        }
        return true;
      });
    },

    filteredErrorEntries: (state): ErrorEntry[] => {
      const query = state.errorsSearch.trim().toLowerCase();
      if (!query) {
        return state.errorEntries;
      }
      return state.errorEntries.filter(entry => entry.message.toLowerCase().includes(query) || entry.stack.toLowerCase().includes(query));
    },

    selectedNetworkEntry(state): NetworkEntry | null {
      if (!state.selectedNetworkEntryId) return null;
      return state.networkEntries.find(e => e.id === state.selectedNetworkEntryId) ?? null;
    },
  },

  actions: {
    /**
     * Fetch + unzip + normalize the insights.zip at the given URL.
     *
     * Cache: if `url` matches `loadedFromUrl` and status is `ready`, this is
     * a no-op (no re-parse on Video → Logs toggles).
     *
     * Race guard: claims the URL synchronously before the await, then checks
     * for staleness after each await. If a newer call lands first, the
     * stale call's result is discarded. Pairs with an `AbortController` so
     * `reset()` can also cancel the in-flight fetch.
     */
    async loadFromUrl(url: string) {
      // Cache hit: same URL already loaded successfully
      if (url && this.loadedFromUrl === url && this.status === 'ready') {
        return;
      }
      // Empty URL → no artifact attached
      if (!url) {
        this.status = 'empty';
        return;
      }

      // Cancel any in-flight fetch and claim the new URL
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.loadedFromUrl = url;
      this.status = 'loading';
      this.errorMessage = '';
      this.errorRetryable = true;

      try {
        const arrayBuffer = await downloadZip(url, controller.signal);
        if (this.loadedFromUrl !== url) return; // superseded

        const raw = await extract(arrayBuffer);
        if (this.loadedFromUrl !== url) return;

        // The SDK embeds the recording start time in each filename inside the
        // zip (`session-{sessionStartMs}-{id}-…`). That's our time anchor —
        // "0:00" in the log viewer aligns to the exact instant the recording
        // began. When it's missing (SDK filename convention broke, or the
        // zip was hand-assembled), fall back to a sentinel far-future anchor
        // so every `Math.max(0, entry.time - anchor)` clamps to 0. Log
        // content still renders; only the per-entry offsets collapse to
        // zero. Strictly better than throwing a "corrupt archive" error.
        let anchorMs: number;
        if (raw.sessionStartMs != null) {
          anchorMs = raw.sessionStartMs;
        } else {
          anchorMs = Number.MAX_SAFE_INTEGER;
          debug('[useLogsStore] zip has no session filename prefix — rendering logs with 00:00 timestamps');
        }

        // Defend against any unexpected transform failure (e.g., a malformed
        // entry the type guards didn't catch). Rethrow as a corrupt-zip error
        // so it routes through the non-retryable error UI instead of leaking
        // out as an "unknown / retry" loop the user can't escape.
        let normalized: LogData;
        try {
          normalized = normalize(raw, anchorMs);
        } catch (parseErr) {
          throw new InsightsCorruptZipError(`Failed to transform logs: ${(parseErr as Error).message}`);
        }
        if (this.loadedFromUrl !== url) return;

        const isEmpty = normalized.console.length === 0 && normalized.network.length === 0 && normalized.errors.length === 0;

        this.$patch({
          consoleEntries: normalized.console,
          networkEntries: normalized.network,
          errorEntries: normalized.errors,
          browserInfo: normalized.browser,
          status: isEmpty ? 'empty' : 'ready',
        });
        this.abortController = null;
      } catch (e) {
        // Ignore intentional cancellation
        if ((e as Error)?.name === 'AbortError') return;
        // Bail if a newer call has superseded us
        if (this.loadedFromUrl !== url) return;

        const described = describeError(e);
        this.status = 'error';
        this.errorMessage = described.message;
        this.errorRetryable = described.retryable;
        this.abortController = null;

        // Telemetry — surfaces aggregate failure rates without leaking PII
        const { track } = useTrack();
        track(TrackEvents.LOGS_PARSE_FAILED, { reason: described.kind, url });
      }
    },

    reset() {
      this.abortController?.abort();
      this.abortController = null;
      this.status = 'idle';
      this.loadedFromUrl = '';
      this.errorMessage = '';
      this.errorRetryable = true;
      this.consoleEntries = [];
      this.networkEntries = [];
      this.errorEntries = [];
      this.browserInfo = emptyBrowserInfo();
      this.selectedNetworkEntryId = null;
      this.consoleFilter = 'all';
      this.networkFilter = 'all';
      this.consoleSearch = '';
      this.networkSearch = '';
      this.errorsSearch = '';
    },

    selectNetworkEntry(id: string) {
      this.selectedNetworkEntryId = id;
    },

    clearSelectedNetworkEntry() {
      this.selectedNetworkEntryId = null;
    },

    /**
     * Clear the search state for a specific sub-tab. Called when the user
     * switches tabs so each tab's search is independent.
     */
    clearSearchForTab(tab: LogSubTab) {
      if (tab === 'console') {
        this.consoleSearch = '';
      } else if (tab === 'network') {
        this.networkSearch = '';
      } else if (tab === 'errors') {
        this.errorsSearch = '';
      }
    },
  },
});
